import { memo, useCallback, useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  DESTRUCTIVE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Button, Group, Modal, Space, Text } from "@mantine/core";

import { IN_DEVELOPMENT } from "../../../config";
import GatedEntities from "../GatedEntities";
import {
  useGateDeleteMutation,
  useGatedEntitiesQuery,
} from "../queries/gates.graphql.generated";

export interface DeleteModalProps {
  gateId: string;
  open: boolean;
  onClose: () => void;
}

const DeleteModal = ({ open, onClose, gateId }: DeleteModalProps) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [deleteMutation] = useGateDeleteMutation();
  const { data: entitiesData } = useGatedEntitiesQuery({
    variables: { gateId },
    fetchPolicy: "cache-and-network",
  });

  const handleDeleteGate = useCallback(async () => {
    try {
      setIsDeleting(true);
      await deleteMutation({
        variables: {
          id: gateId,
        },
      });
      onClose();
      location.reload();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      await logError({ error, message: "delete failed" });
      IN_DEVELOPMENT && console.log("🚨 [ERROR][HANDLE_DELETE]: ", error);
    }
  }, [onClose, gateId]);

  const linkedEntities = entitiesData?.gatedEntities.length ?? 0;
  const hasLinkedEntities = linkedEntities > 0;

  return (
    <Modal
      size="lg"
      opened={open}
      title={
        <Text size="lg" weight={500}>
          Delete gate
        </Text>
      }
      withCloseButton={false}
      onClose={onClose}
    >
      <div>
        <Text size="sm">
          Are you sure you want to delete this gate?{" "}
          {hasLinkedEntities && (
            <>
              The following item{linkedEntities > 1 ? "s" : ""} will be
              accessible to{" "}
              <Text span weight={500}>
                anyone with a link
              </Text>
              .{" "}
            </>
          )}
          You can&apos;t undo this.
        </Text>
        {hasLinkedEntities && (
          <>
            <Space h="sm" />
            <GatedEntities gateId={gateId} showLabel={false} />
          </>
        )}
        <ModalSection>
          <Group noWrap>
            <Button
              fullWidth
              color={SECONDARY_COLOR}
              size="xl"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              color={DESTRUCTIVE_COLOR}
              size="xl"
              loading={isDeleting}
              onClick={handleDeleteGate}
            >
              Delete gate
            </Button>
          </Group>
        </ModalSection>
      </div>
    </Modal>
  );
};

export default memo(DeleteModal);
